/**
 * The chat's state machine, as a pure reducer so it can be tested without React or a browser.
 * Everything the panel renders is derived from this state; the hook (useChatSession) only
 * dispatches actions around network calls.
 */

import type { Failure, TranscriptTurn } from "@/lib/api/chatClient";
import type { ChatCopy, Language } from "./copy";

export type Role = "assistant" | "user";
export type Mode = "ai" | "form";
export type Phase = "idle" | "starting" | "ready" | "sending";
export type CardState = "hidden" | "offered" | "required" | "submitting" | "saved";

export interface ChatMessage {
  id: string;
  role: Role;
  text: string;
  /** Notices the widget itself adds (form mode, cap, saved) — styled like the assistant, never sent to the model. */
  notice?: boolean;
  /** A visitor message that never reached the assistant. Still the visitor's words, so it rides in the note to Jorge. */
  unsent?: boolean;
}

export interface ChatState {
  open: boolean;
  phase: Phase;
  conversationId: string | null;
  mode: Mode;
  turnsLeft: number;
  userTurns: number;
  messages: ChatMessage[];
  card: CardState;
  cardDismissed: boolean;
  cardError: string | null;
  error: string | null;
  ended: boolean;
  /** What the visitor said the note is about, for the links shown while Jorge reads it. */
  topic: string | null;
  /** The assistant-away notice for a failed start is shown once, however many times the start is retried. */
  startFailed: boolean;
  /** Messages that failed in a row; the second one makes the card required. */
  sendFailures: number;
}

export type ChatAction =
  | { type: "open" }
  | { type: "close" }
  | { type: "start" }
  | { type: "started"; conversationId: string; mode: Mode; turnsLeft: number; notice: string | null }
  | { type: "start_retry" }
  | { type: "start_failed"; notice: string }
  | { type: "send"; id: string; text: string }
  | { type: "kept"; id: string; text: string }
  | { type: "reply"; id: string; text: string; turnsLeft: number }
  | { type: "form_mode"; id: string; notice: string; turnsLeft: number }
  | { type: "send_failed"; id: string; error: string }
  | { type: "require_card" }
  | { type: "dismiss_card" }
  | { type: "card_submit" }
  | { type: "card_saved"; id: string; text: string; topic?: string | null }
  | { type: "card_failed"; error: string }
  | { type: "ended" };

let counter = 0;
export function nextId(prefix = "m"): string {
  counter += 1;
  return `${prefix}${counter}`;
}

export function initialState(introText: string): ChatState {
  return {
    open: false,
    phase: "idle",
    conversationId: null,
    mode: "ai",
    turnsLeft: 8,
    userTurns: 0,
    messages: [{ id: nextId("intro"), role: "assistant", text: introText }],
    card: "hidden",
    cardDismissed: false,
    cardError: null,
    error: null,
    ended: false,
    topic: null,
    startFailed: false,
    sendFailures: 0,
  };
}

function notice(state: ChatState, id: string, text: string): ChatMessage[] {
  return [...state.messages, { id, role: "assistant", text, notice: true }];
}

// A card that already collected the visitor's details never comes back.
function requireCard(card: CardState): CardState {
  return card === "saved" || card === "submitting" ? card : "required";
}

export function reduce(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "open":
      return { ...state, open: true };
    case "close":
      return { ...state, open: false };
    case "start":
    case "start_retry":
      return { ...state, phase: "starting", error: null };
    case "started": {
      const formMode = action.mode === "form";
      // A start that failed earlier and works on a reopen: the card it forced stays on screen, as an offer.
      const recovered = !formMode && state.startFailed && state.card === "required";
      return {
        ...state,
        phase: "ready",
        conversationId: action.conversationId,
        mode: action.mode,
        turnsLeft: action.turnsLeft,
        messages: formMode && action.notice ? notice(state, nextId("n"), action.notice) : state.messages,
        card: formMode ? requireCard(state.card) : recovered ? "offered" : state.card,
      };
    }
    case "start_failed":
      // No conversation on the server; the card still works through the contact-form fallback.
      return {
        ...state,
        phase: "ready",
        mode: "form",
        startFailed: true,
        messages: state.startFailed ? state.messages : notice(state, nextId("n"), action.notice),
        card: requireCard(state.card),
      };
    case "send":
      return {
        ...state,
        phase: "sending",
        error: null,
        userTurns: state.userTurns + 1,
        // Sending the same words again replaces the bubble that failed, instead of showing them twice.
        messages: [...state.messages.filter((m) => !(m.unsent && m.text === action.text)), { id: action.id, role: "user", text: action.text }],
      };
    case "kept":
      // Typed while the conversation was starting, which then came back without an assistant: shown,
      // never sent to the model, and carried to Jorge with the card.
      return { ...state, messages: [...state.messages, { id: action.id, role: "user", text: action.text, unsent: true }] };
    case "reply": {
      const reply: ChatMessage = { id: action.id, role: "assistant", text: action.text };
      const messages = [...state.messages, reply];
      // Offered after the first answer (Jorge's call, 2026-09-22); never re-offered once dismissed.
      const offer = state.card === "hidden" && !state.cardDismissed && state.userTurns >= 1;
      return {
        ...state,
        phase: "ready",
        turnsLeft: action.turnsLeft,
        messages,
        sendFailures: 0,
        card: offer ? "offered" : state.card,
      };
    }
    case "form_mode":
      return {
        ...state,
        phase: "ready",
        mode: "form",
        turnsLeft: action.turnsLeft,
        messages: notice(state, action.id, action.notice),
        card: requireCard(state.card),
      };
    case "send_failed": {
      // The text goes back in the composer (the hook does that); the card comes up so a second try
      // is not the only way forward, and after two failures in a row it is the way forward.
      const sendFailures = state.sendFailures + 1;
      const card: CardState =
        sendFailures >= 2 ? requireCard(state.card) : state.card === "hidden" ? "offered" : state.card;
      return {
        ...state,
        phase: "ready",
        error: action.error,
        sendFailures,
        card,
        messages: state.messages.map((m) => (m.id === action.id ? { ...m, unsent: true } : m)),
      };
    }
    case "require_card":
      return { ...state, card: requireCard(state.card), cardError: null };
    case "dismiss_card":
      return state.card === "offered" ? { ...state, card: "hidden", cardDismissed: true } : state;
    case "card_submit":
      return { ...state, card: "submitting", cardError: null };
    case "card_saved":
      return {
        ...state,
        card: "saved",
        cardError: null,
        topic: action.topic ?? state.topic,
        messages: notice(state, action.id, action.text),
      };
    case "card_failed":
      return { ...state, card: "required", cardError: action.error };
    case "ended":
      return { ...state, ended: true };
    default:
      return state;
  }
}

// ---- contact card validation (bilingual messages come from copy) -------------------------

export interface CardFields {
  name: string;
  email: string;
  phone: string;
  business: string;
  message: string;
  hp: string;
  /** The note's context when the assistant is away: chip ids, and a site to look at. All optional. */
  about: string;
  timing: string;
  site: string;
}

export type CardErrors = Partial<Record<"name" | "email" | "phone" | "site", string>>;

export const EMPTY_CARD: CardFields = { name: "", email: "", phone: "", business: "", message: "", hp: "", about: "", timing: "", site: "" };

/** A domain or a URL Jorge can open: "example.com", "www.example.com/menu", "https://maps.app.goo.gl/…". */
export const isValidSite = (value: string): boolean => /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(value.trim());

/**
 * The note Jorge reads, as one message the Lambda already accepts: the two taps and the site as labelled
 * lines, then the visitor's own words. Nothing new for the Lambda or the CRM to store — the context rides in
 * the message, and the transcript travels beside it as before.
 */
export function composeNote(fields: CardFields, copy: ChatCopy): string {
  const about = copy.aboutChips.find((c) => c.id === fields.about)?.label;
  const timing = copy.timingChips.find((c) => c.id === fields.timing)?.label;
  const site = fields.site.trim();
  const lines = [
    about ? `${copy.noteLabels.about}: ${about}` : null,
    timing ? `${copy.noteLabels.timing}: ${timing}` : null,
    site ? `${copy.noteLabels.site}: ${site}` : null,
  ].filter((l): l is string => l !== null);
  const message = fields.message.trim();
  return [...lines, ...(message ? [lines.length ? "" : null, message].filter((l): l is string => l !== null) : [])].join("\n");
}

/**
 * The card's note plus any message that never reached the assistant. After a failed send the card has
 * no message box, and the /chat lead email is built from the server's history, which never saw that
 * message: without this, the question the visitor was trying to ask reaches Jorge on neither path.
 */
export function leadNote(fields: CardFields, messages: ChatMessage[], copy: ChatCopy): string {
  const note = composeNote(fields, copy);
  const unsent = Array.from(new Set(messages.filter((m) => m.unsent && m.role === "user" && !note.includes(m.text)).map((m) => m.text)));
  if (unsent.length === 0) return note;
  return [note, `${note ? "\n" : ""}${copy.notSent}:`, ...unsent].filter(Boolean).join("\n");
}

// Same shapes the Lambda enforces, so a value that passes here is accepted there.
export const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
export const isValidPhone = (value: string): boolean => /^[+]?[0-9\-\s().]{7,30}$/.test(value.trim());

export function validateCard(fields: CardFields, copy: ChatCopy): CardErrors {
  const errors: CardErrors = {};
  if (fields.name.trim().length < 2) errors.name = copy.errors.name;
  if (!isValidEmail(fields.email)) errors.email = copy.errors.email;
  if (fields.phone.trim() && !isValidPhone(fields.phone)) errors.phone = copy.errors.phone;
  if (fields.site.trim() && !isValidSite(fields.site)) errors.site = copy.errors.site;
  return errors;
}

/** A start worth one more try: the service was unreachable or broke, not a refusal. */
export function retryableStart(result: { failure: Failure; status: number }): boolean {
  return result.failure === "network" || result.status >= 500;
}

export const START_RETRY_MS = 2_000;

/** What Jorge reads under the note when the lead goes through the contact form. Notices are the widget's, not the chat's. */
export function transcriptOf(messages: ChatMessage[]): TranscriptTurn[] {
  return messages.filter((m) => !m.notice).map((m) => ({ role: m.role === "user" ? "visitor" : "assistant", text: m.text }));
}

// The Lambdas' 400 messages are English; a Spanish visitor gets the matching line from their own copy.
const SERVER_ERROR_FIELDS: Array<[RegExp, keyof ChatCopy["errors"]]> = [
  [/name/i, "name"],
  [/email/i, "email"],
  [/phone/i, "phone"],
];

/**
 * What the visitor sees when a request fails. The server's words only for a 400, which is about
 * something the visitor can fix; everything else (5xx, timeouts, a dropped connection) is the
 * widget's own localized line, never a raw status or "Something went wrong" in English.
 */
export function failureText(
  result: { failure: Failure; status: number; error: string | null },
  copy: ChatCopy,
  language: Language,
  otherwise: string = copy.errors.generic,
): string {
  if (result.failure === "network" || result.failure === "timeout") return copy.errors.network;
  if (result.status === 400 && result.error) {
    if (language === "en") return result.error;
    const field = SERVER_ERROR_FIELDS.find(([re]) => re.test(result.error ?? ""))?.[1];
    return field ? copy.errors[field] : copy.errors.generic;
  }
  return otherwise;
}

/**
 * True when the composer should accept typing. Deliberately true while the conversation is still
 * starting: send() waits for it, and hiding the composer for that second showed "use the card
 * above" with no card on screen.
 */
export function canCompose(state: ChatState): boolean {
  return state.mode === "ai" && state.turnsLeft > 0;
}
